import useTranslation from 'next-translate/useTranslation';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DatahubEntity, VolumeAction } from 'src/interfaces/Datahub';
import { IPlaybackRateOption } from 'src/interfaces/PlaybackRate';
import ApplicationService from 'src/services/ApplicationService';
import {
  SELECTOR_PLAYBACK_PAUSED,
  SELECTOR_PLAYBACK_PLAYBACK_RATE,
  SELECTOR_PLAYBACK_VOLUME_PERCENT,
  SELECTOR_PLAYBACK_READY,
  SELECTOR_PLAYBACK_IS_MUTED,
} from 'src/store/reducers/playback';
import { debounce } from 'src/utils/debounceUtils';
import useTracking from '../useTracking';
import useUserPreferences from '../useUserPreferences';
import { PLAYBACK_RATE_OPTIONS } from './constants';
import { labelToRate } from './utils';

const usePlayback = () => {
  const paused = useSelector(SELECTOR_PLAYBACK_PAUSED);
  const ready = useSelector(SELECTOR_PLAYBACK_READY);
  const volumePercent = useSelector(SELECTOR_PLAYBACK_VOLUME_PERCENT);
  const isMuted = useSelector(SELECTOR_PLAYBACK_IS_MUTED);
  const playbackRate = useSelector(SELECTOR_PLAYBACK_PLAYBACK_RATE);
  const { trackDatahub } = useTracking();
  const { t } = useTranslation('common');
  const { volumePercent: preferenceVolumePercent } = useUserPreferences();

  const debouncedTrackVolume = useCallback(
    debounce(() => trackDatahub(DatahubEntity.VOLUME, VolumeAction.CHANGE), 500),
    [],
  );

  const togglePlay = useCallback(() => {
    ApplicationService.player?.togglePlay();
  }, []);

  const setVolumePercent = useCallback((percent: number) => {
    ApplicationService.player?.setVolumePercent(percent);
    ApplicationService.userPreferences.updatePreferences({ volumePercent: percent });
    debouncedTrackVolume();
  }, []);

  const raiseVolumePercent = useCallback((percent: number) => {
    let newVolume = (ApplicationService.player?.volumePercent || 0) + percent;
    if (newVolume > 100) newVolume = 100;

    setVolumePercent(newVolume);
  }, []);

  const reduceVolumePercent = useCallback((percent: number) => {
    let newVolume = (ApplicationService.player?.volumePercent || 0) - percent;
    if (newVolume < 0) newVolume = 0;

    setVolumePercent(newVolume);
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    ApplicationService.player?.setPlaybackRate(rate);
    ApplicationService.userPreferences.updatePreferences({ playbackRate: rate });
  }, []);

  const playbackRateOptions: IPlaybackRateOption[] = PLAYBACK_RATE_OPTIONS.map((rate) => {
    return { rate, label: labelToRate(rate, t('settings.normal')) };
  });

  const playbackRateLabel = useMemo(() => {
    return labelToRate(playbackRate, t('settings.normal'));
  }, [playbackRate, t]);

  const unmuted = useCallback(() => {
    setVolumePercent(preferenceVolumePercent);
    ApplicationService.player?.unmuted();
  }, [setVolumePercent, preferenceVolumePercent]);

  return {
    paused,
    ready,
    volumePercent,
    setVolumePercent,
    raiseVolumePercent,
    reduceVolumePercent,
    togglePlay,
    setPlaybackRate,
    playbackRate,
    playbackRateOptions,
    playbackRateLabel,
    isMuted,
    unmuted,
  };
};

export default usePlayback;
